/** Outgoing kinds that paint through a `<video>` we can freeze without a second decoder. */
export function shouldArmFrozenOutgoingStill(args: {
  prevKind: string
  stillUrl: string | null
}): boolean {
  if (args.stillUrl == null || !args.stillUrl.startsWith('data:image/')) {
    return false
  }
  return args.prevKind === 'video-blob' || args.prevKind === 'hls'
}

function isHiddenOutgoingVideo(video: HTMLVideoElement): boolean {
  return video.classList.contains('hidden')
    || video.classList.contains('player-video-offscreen-preload')
}

/**
 * JPEG data URL of the visible `<video>` frame, or null when the element is not paintable
 * (native Media3 surface, undecoded, or a tainted canvas).
 */
export function capturePaintableVideoStill(
  root: { querySelectorAll(selector: string): { length: number, item(index: number): Element | null } } | null | undefined
): string | null {
  if (root == null || typeof document === 'undefined') {
    return null
  }
  const videos = root.querySelectorAll('video')
  for (let i = 0; i < videos.length; i++) {
    const video = videos.item(i)
    if (!(video instanceof HTMLVideoElement) || isHiddenOutgoingVideo(video)) {
      continue
    }
    const still = captureVideoElementStill(video)
    if (still != null) {
      return still
    }
  }
  return null
}

export function captureVideoElementStill(video: HTMLVideoElement): string | null {
  if (video.readyState < 2 || video.videoWidth < 2 || video.videoHeight < 2) {
    return null
  }
  try {
    const canvas = document.createElement('canvas')
    const width = Math.min(video.videoWidth, 1280)
    canvas.width = width
    canvas.height = Math.max(1, Math.round(width * (video.videoHeight / video.videoWidth)))
    const ctx = canvas.getContext('2d')
    if (ctx == null) {
      return null
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    const url = canvas.toDataURL('image/jpeg', 0.72)
    return url.startsWith('data:image/') ? url : null
  }
  catch {
    return null
  }
}
